import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, Trash2, Clock } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { ScrollArea } from './ui/scroll-area';

export function CartDrawer({ onCheckout }) {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, updateHours, getTotal } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    }).format(price);
  };

  const calculateItemTotal = (item) => {
    let total = item.price * item.quantity;
    if (item.additionalHourPrice && item.hours > 1) {
      total += item.additionalHourPrice * (item.hours - 1) * item.quantity;
    }
    return total;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            data-testid="cart-backdrop"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:max-w-md bg-surface/95 backdrop-blur-xl border-l border-white/10 z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
              <div>
                <h2 className="font-display text-xl sm:text-2xl text-white">Your Estimate</h2>
                <p className="text-white/50 text-sm">{items.length} items</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-white/50 hover:text-white transition-colors"
                data-testid="close-cart-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Cart Items */}
            <ScrollArea className="flex-1 p-6">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-white/50">Your estimate is empty</p>
                  <p className="text-white/30 text-sm mt-2">Add experiences to get started</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-4 pb-6 border-b border-white/10"
                      data-testid={`cart-item-${item.id}`}
                    >
                      {/* Image */}
                      {item.image && (
                        <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium text-sm mb-1 truncate">
                          {item.name}
                        </h3>
                        <p className="text-gold text-sm font-medium mb-1">
                          {item.quotePending ? 'Quote Pending' : formatPrice(calculateItemTotal(item))}
                        </p>
                        {/* Price breakdown */}
                        {item.additionalHourPrice > 0 && (
                          <p className="text-white/40 text-xs mb-2">
                            {formatPrice(item.price)} 1st hr
                            {item.hours > 1 && ` + ${formatPrice(item.additionalHourPrice * (item.hours - 1))} (${item.hours - 1} extra hr${item.hours > 2 ? 's' : ''})`}
                          </p>
                        )}

                        {/* Hours selector (if applicable) */}
                        {item.additionalHourPrice > 0 && (
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="w-3 h-3 text-white/40" />
                            <div className="flex items-center gap-2 bg-background/50 p-1">
                              <button
                                onClick={() => updateHours(item.id, item.hours - 1)}
                                className="p-1 text-white/50 hover:text-white disabled:opacity-30"
                                disabled={item.hours <= 1}
                                data-testid={`hours-minus-${item.id}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="text-white text-xs w-12 text-center">
                                {item.hours} hr{item.hours > 1 ? 's' : ''}
                              </span>
                              <button
                                onClick={() => updateHours(item.id, item.hours + 1)}
                                className="p-1 text-white/50 hover:text-white"
                                data-testid={`hours-plus-${item.id}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Quantity and Remove */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 bg-background/50 p-1">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-1 text-white/50 hover:text-white"
                              data-testid={`qty-minus-${item.id}`}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-white text-xs w-6 text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-1 text-white/50 hover:text-white"
                              data-testid={`qty-plus-${item.id}`}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="p-2 text-white/30 hover:text-red-400 transition-colors"
                            data-testid={`remove-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Footer */}
            {items.length > 0 && (
              <div className="p-6 border-t border-white/10 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Estimated Total</span>
                  <span className="font-display text-2xl text-gold">
                    {formatPrice(getTotal())}
                  </span>
                </div>
                {items.some(i => i.quotePending) && (
                  <p className="text-amber-400/80 text-xs">
                    + Items pending quote (not included in total)
                  </p>
                )}
                <p className="text-white/30 text-xs">
                  Final pricing may vary based on location and event requirements
                </p>
                <button
                  onClick={onCheckout}
                  className="w-full py-4 bg-gold text-background font-medium hover:bg-gold-light transition-colors"
                  data-testid="checkout-btn"
                >
                  Review & Submit Estimate
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
